import { useState } from 'react'
import { useQuery, useQueryClient, useSuspenseQuery, type QueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import type { CoverageLibraryEntry, CoverageListType } from '@headline/schemas'
import { FilterPills } from '@/components/FilterPills'
import { LibraryTable, type LibraryColumn } from '@/components/LibraryTable'
import { PageHeader } from '@/components/PageHeader'
import { CopyListDialog } from '@/components/reports/CopyListDialog'
import { ListCreateDialog, ListEditDialog } from '@/components/reports/CoverageField'
import { TYPE_LABEL, TYPE_TINT } from '@/components/reports/coverage-list-meta'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import { COUNTRIES, LANGUAGES } from '@/lib/coverage-vocab'
import { accountsQuery, coverageLibraryQuery, coverageListQuery } from '@/lib/queries'
import { cn } from '@/lib/utils'

function optionsFor(type: CoverageListType): string[] | undefined {
  return type === 'countries' ? COUNTRIES : type === 'languages' ? LANGUAGES : undefined
}

/**
 * Coverage lists — an INTERNAL, org-level surface (Kevin, 2026-08-29), not a per-workspace resource.
 * Clipbook staff curate them here: a list is either GLOBAL (available to every account) or
 * PROVISIONED to one account. Accounts consume them in a report's coverage; they don't manage the
 * pool. One table across every account, with the owning account as a column. This page's shape —
 * pills + search, in-table empty states — is the LibraryTable standard the other internal pages
 * follow.
 *
 * One request loads the whole library (CLI-855) — it used to be one per account, each re-sending
 * every global list. Rows carry member COUNTS; the edit dialog loads the members by id, prefetched
 * on hover so opening it doesn't wait on the round trip. Search stays client-side and instant.
 */
export const Route = createFileRoute('/app/lists')({
  loader: ({ context }) =>
    Promise.all([
      context.queryClient.ensureQueryData(accountsQuery),
      context.queryClient.ensureQueryData(coverageLibraryQuery),
    ]),
  component: ListsPage,
})

/** `accountName` is null for a global list. */
type ListRow = { accountName: string | null; list: CoverageLibraryEntry }

/** The columns, closed over the page's handlers — a factory so the cells stay plain JSX. */
function listColumns(
  queryClient: QueryClient,
  onEdit: (list: CoverageLibraryEntry) => void,
  onCopy: (list: CoverageLibraryEntry) => void,
): Array<LibraryColumn<ListRow>> {
  const prefetch = (list: CoverageLibraryEntry) =>
    void queryClient.prefetchQuery(coverageListQuery(list.id))
  return [
    {
      key: 'name',
      header: 'List',
      cell: ({ list }) => (
        <>
          <button
            type="button"
            className="font-medium text-foreground underline-offset-4 hover:underline"
            onPointerEnter={() => prefetch(list)}
            onFocus={() => prefetch(list)}
            onClick={() => onEdit(list)}
          >
            {list.name}
          </button>
          {list.role !== null ? (
            <span className="ml-2 text-[10px] text-muted-foreground uppercase">built-in</span>
          ) : list.accountId === null ? (
            <span className="ml-2 rounded-full bg-primary/10 px-1.5 text-[10px] font-medium text-primary uppercase">
              global
            </span>
          ) : null}
        </>
      ),
    },
    {
      key: 'owner',
      header: 'Provisioned to',
      cellClassName: 'text-muted-foreground',
      cell: ({ accountName }) => accountName ?? 'All accounts',
    },
    {
      key: 'type',
      header: 'Type',
      cell: ({ list }) => (
        <Badge variant="secondary" className={cn('text-[10px]', TYPE_TINT[list.type])}>
          {TYPE_LABEL[list.type]}
        </Badge>
      ),
    },
    {
      key: 'members',
      header: 'Members',
      headClassName: 'text-right',
      cellClassName: 'text-right tabular-nums',
      cell: ({ list }) => list.memberCount,
    },
    {
      key: 'usedIn',
      header: 'Used in',
      headClassName: 'text-right',
      cellClassName: 'text-right tabular-nums',
      cell: ({ list }) =>
        list.usedIn > 0 ? (
          <span className="font-medium text-primary">
            {list.usedIn} place{list.usedIn === 1 ? '' : 's'}
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      key: 'actions',
      header: '',
      cellClassName: 'text-right',
      cell: ({ list }) => (
        <Button variant="ghost" size="sm" onClick={() => onCopy(list)}>
          Copy
        </Button>
      ),
    },
  ]
}

function ListsPage() {
  const { data } = useSuspenseQuery(accountsQuery)
  const { data: library } = useSuspenseQuery(coverageLibraryQuery)
  const queryClient = useQueryClient()

  const [typeFilter, setTypeFilter] = useState<CoverageListType | 'all'>('all')
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<CoverageLibraryEntry | null>(null)
  const [copying, setCopying] = useState<CoverageLibraryEntry | null>(null)
  const accounts = data.accounts.map((account) => ({ id: account.id, name: account.name }))

  const accountNames = new Map(accounts.map((account) => [account.id, account.name]))
  // A list whose owner isn't in the caller's accounts listing still shows, under a placeholder.
  const rows: ListRow[] = library.lists.map((list) => ({
    accountName: list.accountId === null ? null : (accountNames.get(list.accountId) ?? 'Unknown'),
    list,
  }))
  const shown = typeFilter === 'all' ? rows : rows.filter(({ list }) => list.type === typeFilter)

  const columns = listColumns(queryClient, setEditing, setCopying)

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <PageHeader
          title="Coverage List Library"
          description="A list can be global to all accounts or provisioned to a single account. Contains referenced LIVE: editing one updates every product that uses it."
        />
        <Button onClick={() => setCreating(true)} disabled={data.accounts.length === 0}>
          New list
        </Button>
      </div>

      <LibraryTable
        rows={shown}
        filtered={typeFilter !== 'all'}
        columns={columns}
        rowKey={({ list }) => list.id}
        searchText={({ accountName, list }) => `${list.name} ${accountName ?? ''}`}
        searchPlaceholder="Search lists…"
        filters={
          <FilterPills
            options={(['all', 'outlets', 'countries', 'languages', 'priority'] as const).map(
              (type) => ({ value: type, label: type === 'all' ? 'All types' : TYPE_LABEL[type] }),
            )}
            value={typeFilter}
            onChange={setTypeFilter}
          />
        }
        emptyAll={
          <>
            No lists yet. Create one here, or in-flow from any coverage field&rsquo;s <b>Lists ▾</b>{' '}
            menu.
          </>
        }
        emptyFiltered="No lists match."
      />

      {creating && data.accounts[0] && (
        <ListCreateDialog
          type="outlets"
          accountId={data.accounts[0].id}
          accountPicker={data.accounts.map((account) => ({ id: account.id, name: account.name }))}
          typePicker
          allowGlobal
          initialMembers={[]}
          onCreated={() => setCreating(false)}
          onClose={() => setCreating(false)}
        />
      )}
      {editing && <LibraryListEditor listId={editing.id} onClose={() => setEditing(null)} />}
      {copying && (
        <CopyListDialog
          list={copying}
          accounts={accounts}
          onClose={() => setCopying(null)}
          queryClient={queryClient}
        />
      )}
    </div>
  )
}

/**
 * The edit dialog for one library row: loads the list's members (rows carry only a count) and
 * opens once they arrive — usually already cached by the name's hover prefetch.
 */
function LibraryListEditor({ listId, onClose }: { listId: string; onClose: () => void }) {
  const { data: list, isError } = useQuery(coverageListQuery(listId))
  if (isError) {
    return (
      <Dialog
        open
        onClose={onClose}
        role="alertdialog"
        title="Couldn’t load this list"
        description="The list may have been deleted, or the request failed. Close and try again."
      >
        <Button onClick={onClose}>Close</Button>
      </Dialog>
    )
  }
  if (!list) return null
  return (
    <ListEditDialog
      list={list}
      accountId={null}
      options={optionsFor(list.type)}
      onClose={onClose}
    />
  )
}
